<template>
    <article>
        <v-tabs v-model="tab" height="40" slider-size="2" show-arrows class="mb-4">
            <v-tab :to="{hash: 'ce'}" tab-value="ce">Community Edition</v-tab>
            <v-tab :to="{hash: 'ee'}" tab-value="ee">Enterprise Edition</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
                <v-tab-item value="ce" eager transition="none">
                <h3>Step #1: Initialize Docker Swarm</h3>


                <p>
                    If you haven't already initialized Docker Swarm on your manager node, run the following command on the manager node:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
docker swarm init
                    </pre>
                </CodeSnippet>

                <p>
                    This initializes a new Docker Swarm and joins the current node as a manager.
                </p>

                <h3>Step #2: Deploy the Coroot Stack</h3>

                <p>
                    Deploy the Coroot stack to your cluster by running the following command on the manager node.
                    Before applying, you can review the configuration file in Coroot's GitHub repository:
                    <a href="https://github.com/coroot/coroot/blob/main/deploy/docker-swarm-stack.yaml" target="_blank">docker-swarm-stack.yaml</a>
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
curl -fsS https://raw.githubusercontent.com/coroot/coroot/main/deploy/docker-swarm-stack.yaml | docker stack deploy -c - coroot
                    </pre>
                </CodeSnippet>

                <h3>Step #3: Validate the deployment</h3>

                <p>
                    After deploying the stack, you can use <var>docker stack ls</var> to list the deployed stacks in your Docker Swarm cluster.
                    Here's an example of how the output might look:
                </p>

                <pre v-hl.sh>
NAME      SERVICES
coroot    3
                </pre>

                <h3>Step #4: Installing coroot-node-agent</h3>

                <p>
                    Since Docker Swarm doesn't support privileged containers, you'll have to manually deploy
                    <var>coroot-node-agent</var> on each cluster node.
                    Just replace <var>&lt;NODE_IP&gt;</var> with any node's IP address in the Docker Swarm cluster.
                </p>

                <CodeSnippet>
                    <pre v-hl.bash>
docker run --detach --name coroot-node-agent \
  --pull=always \
  --privileged --pid host \
  -v /sys/kernel/tracing:/sys/kernel/tracing:rw \
  -v /sys/kernel/debug:/sys/kernel/debug:rw \
  -v /sys/fs/cgroup:/host/sys/fs/cgroup:ro \
  ghcr.io/coroot/coroot-node-agent \
  --cgroupfs-root=/host/sys/fs/cgroup \
  --collector-endpoint=http://&lt;NODE_IP&gt;:8080
                    </pre>
                </CodeSnippet>

                <h3>Step #5: Accessing Coroot</h3>

                <p>
                    Access Coroot through any node in your Docker Swarm cluster using its published port: http://&lt;node_ip&gt;:8080.
                </p>

                <h2>Uninstall Coroot</h2>

                <p>To uninstall Coroot run the following command:</p>

                <CodeSnippet>
                    <pre v-hl.bash>
docker stack rm coroot
                    </pre>
                </CodeSnippet>

            </v-tab-item>

            <v-tab-item value="ee" eager transition="none">
                <v-alert color="info" outlined text class="mt-5">
                    The Enterprise Edition is a paid subscription (from $1 per CPU core/month) that offers extra features and priority support.
                    <a href="https://coroot.com/account" target="_blank" class="font-weight-bold">Start</a> your free trial today.
                </v-alert>

                <h3>Step #1: Initialize Docker Swarm</h3>


                <p>
                    If you haven't already initialized Docker Swarm on your manager node, run the following command on the manager node:
                </p>

                <CodeSnippet>
                    <pre v-hl.sh>
docker swarm init
                    </pre>
                </CodeSnippet>

                <p>
                    This initializes a new Docker Swarm and joins the current node as a manager.
                </p>

                <h3>Step #2: Deploy the Coroot Stack</h3>

                <v-alert color="info" outlined text class="mt-5">
                    To install Coroot Enterprise Edition, you'll need a license (from $1 per CPU core/month).
                    <a href="https://coroot.com/account" target="_blank" class="font-weight-bold">Start</a> your free trial today.
                </v-alert>

                <div class="subtitle-1">License key:</div>
                <v-form>
                    <v-text-field
                        v-model="license_key"
                        placeholder="COROOT-11111111-1111-1111-1111-111111111111"
                        outlined
                        dense
                    />
                </v-form>


                <p>
                    Deploy the Coroot stack to your cluster by running the following command on the manager node.
                    Before applying, you can review the configuration file in Coroot's GitHub repository:
                    <a href="https://github.com/coroot/coroot-ee/blob/main/deploy/docker-swarm-stack.yaml" target="_blank">docker-swarm-stack.yaml</a>
                </p>

                <CodeSnippet>
                    <pre class="hl">
curl -fsS https://raw.githubusercontent.com/coroot/coroot-ee/main/deploy/docker-swarm-stack.yaml | \
    LICENSE_KEY="{{license_key || 'COROOT-LICENSE-KEY-HERE'}}" docker stack deploy -c - coroot-ee</pre>
                </CodeSnippet>

                <h3>Step #3: Validate the deployment</h3>

                <p>
                    After deploying the stack, you can use <var>docker stack ls</var> to list the deployed stacks in your Docker Swarm cluster.
                    Here's an example of how the output might look:
                </p>

                <pre v-hl.sh>
NAME        SERVICES
coroot-ee   4
                </pre>

                <h3>Step #4: Installing coroot-node-agent</h3>

                <p>
                    Since Docker Swarm doesn't support privileged containers, you'll have to manually deploy
                    <var>coroot-node-agent</var> on each cluster node.
                    Just replace <var>&lt;NODE_IP&gt;</var> with any node's IP address in the Docker Swarm cluster.
                </p>

                <CodeSnippet>
                    <pre v-hl.bash>
docker run --detach --name coroot-node-agent \
  --pull=always \
  --privileged --pid host \
  -v /sys/kernel/tracing:/sys/kernel/tracing:rw \
  -v /sys/kernel/debug:/sys/kernel/debug:rw \
  -v /sys/fs/cgroup:/host/sys/fs/cgroup:ro \
  ghcr.io/coroot/coroot-node-agent \
  --cgroupfs-root=/host/sys/fs/cgroup \
  --collector-endpoint=http://&lt;NODE_IP&gt;:8080
                    </pre>
                </CodeSnippet>

                <h3>Step #5: Accessing Coroot</h3>

                <p>
                    Access Coroot through any node in your Docker Swarm cluster using its published port: http://&lt;node_ip&gt;:8080.
                </p>

                <h2>Uninstall Coroot</h2>

                <p>To uninstall Coroot run the following command:</p>

                <CodeSnippet>
                    <pre v-hl.bash>
docker stack rm coroot-ee
                    </pre>
                </CodeSnippet>

            </v-tab-item>
        </v-tabs-items>
    </article>
</template>

<script>

export default {
    data() {
        return {
            license_key: '',
            tab: null,
        }
    },
    computed: {},
}
</script>

<style scoped>
</style>
